import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../useToast';
import { contractService } from '../../services/contracts/ContractService';
import {
  ContractFiltersDto,
  ContractListPagedResponseDto,
  EContractStatus,
  LatestDocusignSendSummaryDto,
  PatchContractResultDto,
} from '../../services/contracts/ContractService';
import { extractApiErrorMessage } from '../../services/utils/apiErrorUtils';
import {
  contractsQueryKeys,
  resolveContractsListStaleTimeMs,
  resolveContractsPrimaryRole,
} from './contractsQueryKeys';

type OperationalTriState = '' | 'yes' | 'no';

type ApplyFiltersOverrides = {
  status?: EContractStatus | '';
  clientSearch?: string;
};

const DEFAULT_PAGE_SIZE = 20;

const buildDefaultFilters = (pageNumber: number, pageSize: number): ContractFiltersDto => ({
  pageNumber,
  pageSize,
  orderBy: 'createdAt',
  isDescending: true,
});

const ensureListDefaults = (filters: ContractFiltersDto, pageSize: number): ContractFiltersDto => ({
  ...filters,
  pageNumber: filters.pageNumber ?? 1,
  pageSize: filters.pageSize ?? pageSize,
  orderBy: filters.orderBy ?? 'createdAt',
  isDescending: filters.isDescending !== false,
});

const applyBkoPolicy = (filters: ContractFiltersDto, isBko: boolean): ContractFiltersDto =>
  isBko ? { ...filters, status: EContractStatus.EmAnalise } : filters;

export function useContractFilters() {
  const { hasRole, user } = useAuth();
  const { showError } = useToast();
  const queryClient = useQueryClient();
  const isBko = hasRole('BKO');
  const primaryRole = useMemo(() => resolveContractsPrimaryRole(user?.roles), [user?.roles]);
  const scopedUserId = user?.id || 'anonymous';

  // Estados de paginação/filtros aplicados.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<ContractFiltersDto>({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Estados para filtros básicos
  const [clientSearchFilter, setClientSearchFilter] = useState('');
  const [tipoContratacaoFilter, setTipoContratacaoFilter] = useState('');
  const [valorMinimoFilter, setValorMinimoFilter] = useState('');
  const [valorMaximoFilter, setValorMaximoFilter] = useState('');
  const [dataInicioFilter, setDataInicioFilter] = useState('');
  const [dataFimFilter, setDataFimFilter] = useState('');
  const [contractIdFilter, setContractIdFilter] = useState('');
  const [iccidFilter, setIccidFilter] = useState('');

  // Estados para filtros avançados
  const [statusFilter, setStatusFilter] = useState<EContractStatus | ''>('');
  const [userNameFilter, setUserNameFilter] = useState('');
  const [enderecoCidadeFilter, setEnderecoCidadeFilter] = useState('');
  const [enderecoEstadoFilter, setEnderecoEstadoFilter] = useState('');
  const [administratorNomeFilter, setAdministratorNomeFilter] = useState('');
  const [searchTermFilter, setSearchTermFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [temTrocaLinhaTriFilter, setTemTrocaLinhaTriFilter] = useState<OperationalTriState>('');
  const [temPendenciaSuspensaoTriFilter, setTemPendenciaSuspensaoTriFilter] = useState<OperationalTriState>('');
  const [temAparelhoCatalogoTriFilter, setTemAparelhoCatalogoTriFilter] = useState<OperationalTriState>('');
  const [effectiveClustersFilter, setEffectiveClustersFilter] = useState<string[]>([]);

  const normalizedListFilters = useMemo(() => {
    const base = contractService.hasFilters(filters)
      ? ensureListDefaults({ ...filters, pageNumber: currentPage }, pageSize)
      : buildDefaultFilters(currentPage, pageSize);
    return applyBkoPolicy(base, isBko);
  }, [currentPage, filters, isBko, pageSize]);

  // Cancelamento de requisição em voo é feito nativamente pelo TanStack Query (o `signal` do
  // queryFn é abortado quando uma chamada mais nova substitui esta) — dispensa o
  // AbortableRequestManager manual que o fluxo antigo (pré-TanStack) usava.
  const fetchListForFilters = useCallback(
    async (requestFilters: ContractFiltersDto) => {
      const preparedFilters = ensureListDefaults(requestFilters, pageSize);
      const effectiveFilters = applyBkoPolicy(preparedFilters, isBko);
      const pageNumber = effectiveFilters.pageNumber ?? 1;

      return queryClient.fetchQuery({
        queryKey: contractsQueryKeys.list(scopedUserId, primaryRole, effectiveFilters),
        queryFn: ({ signal }) => contractService.getListContracts(effectiveFilters, { signal }),
        staleTime: resolveContractsListStaleTimeMs(primaryRole, pageNumber),
      });
    },
    [isBko, pageSize, primaryRole, queryClient, scopedUserId]
  );

  const contractsQuery = useQuery({
    queryKey: contractsQueryKeys.list(scopedUserId, primaryRole, normalizedListFilters),
    queryFn: ({ signal }) => contractService.getListContracts(normalizedListFilters, { signal }),
    staleTime: resolveContractsListStaleTimeMs(primaryRole, normalizedListFilters.pageNumber ?? 1),
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (contractsQuery.error) {
      showError(extractApiErrorMessage(contractsQuery.error, 'Erro ao carregar contratos. Tente novamente.'));
    }
  }, [contractsQuery.error, showError]);

  const contracts = contractsQuery.data ?? null;
  const contractsLoading = contractsQuery.isLoading || (contractsQuery.isFetching && !contractsQuery.data);
  const contractsError = contractsQuery.error
    ? extractApiErrorMessage(
        contractsQuery.error,
        'Não foi possível carregar a lista de contratos. Verifique sua conexão e tente novamente.'
      )
    : null;

  const loadContracts = useCallback(
    async (page: number = 1) => {
      const nextFilters = contractService.hasFilters(filters)
        ? ensureListDefaults(
            {
              ...filters,
              pageNumber: page,
              pageSize,
            },
            pageSize
          )
        : buildDefaultFilters(page, pageSize);

      setFilters(nextFilters);
      setCurrentPage(page);
      await fetchListForFilters(nextFilters);
    },
    [fetchListForFilters, filters, pageSize]
  );

  const retryLoadContracts = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: contractsQueryKeys.listForUser(scopedUserId, primaryRole),
    });
    void loadContracts(currentPage);
  }, [currentPage, loadContracts, primaryRole, queryClient, scopedUserId]);

  const applyFilters = useCallback((overrides?: ApplyFiltersOverrides) => {
    const effectiveStatusFilter =
      overrides?.status !== undefined ? overrides.status : statusFilter;
    if (overrides?.status !== undefined) {
      setStatusFilter(overrides.status);
    }
    if (overrides?.clientSearch !== undefined) {
      setClientSearchFilter(overrides.clientSearch);
    }
    const finalStatusFilter = isBko ? EContractStatus.EmAnalise : (effectiveStatusFilter || undefined);
    const effectiveClientSearch =
      overrides?.clientSearch !== undefined ? overrides.clientSearch : clientSearchFilter;

    const newFilters: ContractFiltersDto = {
      pageNumber: 1,
      pageSize,
      clientSearch: effectiveClientSearch.trim() || undefined,
      tipoContratacao: tipoContratacaoFilter || undefined,
      valorTotalMensalDe:
        valorMinimoFilter && valorMinimoFilter.trim()
          ? (() => {
              const valor = parseFloat(valorMinimoFilter.replace(',', '.'));
              return isNaN(valor) ? undefined : valor;
            })()
          : undefined,
      valorTotalMensalAte:
        valorMaximoFilter && valorMaximoFilter.trim()
          ? (() => {
              const valor = parseFloat(valorMaximoFilter.replace(',', '.'));
              return isNaN(valor) ? undefined : valor;
            })()
          : undefined,
      dataAssinaturaDe: dataInicioFilter && dataInicioFilter.trim() ? new Date(dataInicioFilter) : undefined,
      dataAssinaturaAte: dataFimFilter && dataFimFilter.trim() ? new Date(dataFimFilter) : undefined,
      status: finalStatusFilter,
      userName: userNameFilter.trim() || undefined,
      enderecoCidade: enderecoCidadeFilter.trim() || undefined,
      enderecoEstado: enderecoEstadoFilter.trim() || undefined,
      administratorNome: administratorNomeFilter.trim() || undefined,
      searchTerm: searchTermFilter.trim() || undefined,
      temTrocaLinha:
        temTrocaLinhaTriFilter === 'yes' ? true : temTrocaLinhaTriFilter === 'no' ? false : undefined,
      temPendenciaSuspensaoLinhas:
        temPendenciaSuspensaoTriFilter === 'yes'
          ? true
          : temPendenciaSuspensaoTriFilter === 'no'
            ? false
            : undefined,
      temAparelhoCatalogo:
        temAparelhoCatalogoTriFilter === 'yes'
          ? true
          : temAparelhoCatalogoTriFilter === 'no'
            ? false
            : undefined,
      effectiveClusters:
        effectiveClustersFilter.length > 0 ? [...effectiveClustersFilter] : undefined,
    };

    setFilters(newFilters);
    setCurrentPage(1);
    void fetchListForFilters(newFilters);
  }, [
    administratorNomeFilter,
    clientSearchFilter,
    dataFimFilter,
    dataInicioFilter,
    effectiveClustersFilter,
    enderecoCidadeFilter,
    enderecoEstadoFilter,
    fetchListForFilters,
    isBko,
    pageSize,
    searchTermFilter,
    statusFilter,
    temAparelhoCatalogoTriFilter,
    temPendenciaSuspensaoTriFilter,
    temTrocaLinhaTriFilter,
    tipoContratacaoFilter,
    userNameFilter,
    valorMaximoFilter,
    valorMinimoFilter,
  ]);

  const clearFilters = useCallback(() => {
    setClientSearchFilter('');
    setTipoContratacaoFilter('');
    setValorMinimoFilter('');
    setValorMaximoFilter('');
    setDataInicioFilter('');
    setDataFimFilter('');
    setContractIdFilter('');
    setIccidFilter('');
    if (!isBko) {
      setStatusFilter('');
    }
    setUserNameFilter('');
    setEnderecoCidadeFilter('');
    setEnderecoEstadoFilter('');
    setAdministratorNomeFilter('');
    setSearchTermFilter('');
    setTemTrocaLinhaTriFilter('');
    setTemPendenciaSuspensaoTriFilter('');
    setTemAparelhoCatalogoTriFilter('');
    setEffectiveClustersFilter([]);
    setShowAdvancedFilters(false);

    const emptyFilters: ContractFiltersDto = {
      pageNumber: 1,
      pageSize,
    };
    setFilters(emptyFilters);
    setCurrentPage(1);
    void fetchListForFilters(buildDefaultFilters(1, pageSize));
  }, [fetchListForFilters, isBko, pageSize]);

  const handlePageChange = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, data: { activePage?: string | number }) => {
      const newPage = Number(data.activePage) || 1;
      void loadContracts(newPage);
    },
    [loadContracts]
  );

  const patchContractsStatus = useCallback(
    (contractIds: string[], status: EContractStatus) => {
      const idSet = new Set(contractIds);
      queryClient.setQueriesData<ContractListPagedResponseDto>(
        { queryKey: contractsQueryKeys.listForUser(scopedUserId, primaryRole) },
        (prev: ContractListPagedResponseDto | undefined) => {
          if (!prev?.items?.length) return prev;
          const hasMatch = prev.items.some((item) => idSet.has(item.id));
          if (!hasMatch) return prev;
          return {
            ...prev,
            items: prev.items.map((item) =>
              idSet.has(item.id) ? { ...item, status } : item
            ),
          };
        }
      );
    },
    [primaryRole, queryClient, scopedUserId]
  );

  const patchContractLatestDocusignSend = useCallback(
    (contractId: string, summary: LatestDocusignSendSummaryDto | null | undefined) => {
      queryClient.setQueriesData<ContractListPagedResponseDto>(
        { queryKey: contractsQueryKeys.listForUser(scopedUserId, primaryRole) },
        (prev: ContractListPagedResponseDto | undefined) => {
          if (!prev?.items?.length) return prev;
          const hasMatch = prev.items.some((item) => item.id === contractId);
          if (!hasMatch) return prev;
          return {
            ...prev,
            items: prev.items.map((item) =>
              item.id === contractId
                ? { ...item, latestDocusignSend: summary ?? null }
                : item
            ),
          };
        }
      );
    },
    [primaryRole, queryClient, scopedUserId]
  );

  const patchContractStatuses = useCallback(
    (contractIds: string[], status: EContractStatus) => {
      const ids = new Set(contractIds.filter(Boolean));
      if (ids.size === 0) return;

      queryClient.setQueriesData<ContractListPagedResponseDto>(
        { queryKey: contractsQueryKeys.listForUser(scopedUserId, primaryRole) },
        (prev: ContractListPagedResponseDto | undefined) => {
          if (!prev?.items?.length) return prev;
          const hasMatch = prev.items.some((item) => ids.has(item.id));
          if (!hasMatch) return prev;
          return {
            ...prev,
            items: prev.items.map((item) =>
              ids.has(item.id)
                ? { ...item, status, updatedAt: new Date().toISOString() }
                : item
            ),
          };
        }
      );
    },
    [primaryRole, queryClient, scopedUserId]
  );

  const patchContractListItem = useCallback(
    (result: PatchContractResultDto) => {
      queryClient.setQueriesData<ContractListPagedResponseDto>(
        { queryKey: contractsQueryKeys.listForUser(scopedUserId, primaryRole) },
        (prev: ContractListPagedResponseDto | undefined) => {
          if (!prev?.items?.length) return prev;

          if (result.wasDeleted) {
            const filtered = prev.items.filter((item) => item.id !== result.contractId);
            if (filtered.length === prev.items.length) return prev;
            return { ...prev, items: filtered };
          }

          const hasMatch = prev.items.some((item) => item.id === result.contractId);
          if (!hasMatch) return prev;

          return {
            ...prev,
            items: prev.items.map((item) =>
              item.id === result.contractId
                ? {
                    ...item,
                    valorTotalMensal: result.valorTotalMensal ?? item.valorTotalMensal,
                    totalLinhas: result.totalLinhas,
                    updatedAt: new Date().toISOString(),
                  }
                : item
            ),
          };
        }
      );
    },
    [primaryRole, queryClient, scopedUserId]
  );

  const totalPages = contracts?.totalPages ?? 0;

  return {
    contracts,
    contractsLoading,
    contractsError,
    retryLoadContracts,
    currentPage,
    totalPages,
    pageSize,
    loadContracts,
    patchContractsStatus,
    patchContractLatestDocusignSend,
    patchContractStatuses,
    patchContractListItem,
    handlePageChange,
    filters,
    setFilters,
    clientSearchFilter,
    setClientSearchFilter,
    tipoContratacaoFilter,
    setTipoContratacaoFilter,
    valorMinimoFilter,
    setValorMinimoFilter,
    valorMaximoFilter,
    setValorMaximoFilter,
    dataInicioFilter,
    setDataInicioFilter,
    dataFimFilter,
    setDataFimFilter,
    contractIdFilter,
    setContractIdFilter,
    iccidFilter,
    setIccidFilter,
    statusFilter,
    setStatusFilter,
    userNameFilter,
    setUserNameFilter,
    enderecoCidadeFilter,
    setEnderecoCidadeFilter,
    enderecoEstadoFilter,
    setEnderecoEstadoFilter,
    administratorNomeFilter,
    setAdministratorNomeFilter,
    searchTermFilter,
    setSearchTermFilter,
    showAdvancedFilters,
    setShowAdvancedFilters,
    temTrocaLinhaTriFilter,
    setTemTrocaLinhaTriFilter,
    temPendenciaSuspensaoTriFilter,
    setTemPendenciaSuspensaoTriFilter,
    temAparelhoCatalogoTriFilter,
    setTemAparelhoCatalogoTriFilter,
    effectiveClustersFilter,
    setEffectiveClustersFilter,
    applyFilters,
    clearFilters,
  };
}
